import React, { useState } from "react";
import { useCustomMutation, useShow } from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { Button, Card, Col, Row, Space, Table, Tag, Typography, Modal, Form, Select, Input, message, Badge } from "antd";
import {
    PrinterOutlined,
    SyncOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    RollbackOutlined,
    EditOutlined
} from "@ant-design/icons";
import { Link, useNavigate, useParams } from "react-router-dom";
import { invoiceRegenerationService, RegenerationRequest } from "../../api/invoiceRegenerationService";
import { PageHeader } from "../../components/ui";

const { Text } = Typography;
const { TextArea } = Input;

interface ActionButton {
    label: string;
    onClick: () => void;
    type?: "primary" | "default" | "dashed" | "text" | "link";
    icon?: React.ReactNode;
    danger?: boolean;
    ghost?: boolean;
    key?: string;
}

export const SaleReturnShow: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { queryResult } = useShow({
        resource: "salereturns",
        meta: {
            fields: [
                "id",
                "saleId",
                "status",
                "returnDate",
                "returnReason",
                "documentUrl",
                "createdAt",
                "updatedAt",
                {
                    sale: [
                        "id",
                        "invoiceNo",
                        "customerTPIN",
                        "customerId",
                        "customerName",
                        "salesStatusCode",
                        "currencyTypeCode",
                        "remark",
                        "totalItemCount",
                        "totalTaxableAmount",
                        "totalTax",
                        "totalAmount",
                        "status",
                        "createdAt",
                        "updatedAt",
                        {
                            items: [
                                "id",
                                "inventoryItemId",
                                "itemName",
                                "quantity",
                                "unitPrice",
                                "totalAmount",
                                {
                                    inventoryItem: [
                                        "id",
                                        "itemCode",
                                        "itemName",
                                        "additionalInformation"
                                    ]
                                }
                            ]
                        },
                        {
                            payments: [
                                "id",
                                "paymentMethod",
                                "amount",
                                "currencyTypeCode"
                            ]
                        },
                        {
                            taxes: [
                                "id",
                                "taxTypeCode",
                                "taxableAmount",
                                "taxPercent",
                                "taxAmount"
                            ]
                        }
                    ]
                }
            ]
        }
    });
    const { data, isLoading } = queryResult;
    const record = data?.data;

    const { mutate: approve } = useCustomMutation();
    const { mutate: reject } = useCustomMutation();
    const { mutate: complete } = useCustomMutation();
    const [regenerateModalVisible, setRegenerateModalVisible] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [form] = Form.useForm();

    const handleApprove = () => {
        approve({
            url: `salereturns/${record?.id}/approve`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Sale return approved successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleReject = () => {
        reject({
            url: `salereturns/${record?.id}/reject`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Sale return rejected successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleComplete = () => {
        complete({
            url: `salereturns/${record?.id}/complete`,
            method: "post",
            successNotification: () => ({
                message: "Success",
                description: "Sale return completed successfully",
                type: "success",
            }),
        }, {
            onSuccess: () => {
                queryResult.refetch();
            }
        });
    };

    const handleViewDocument = () => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, '_blank');
        }
    };

    const handleOpenRegenerate = () => {
        form.resetFields();
        setRegenerateModalVisible(true);
    };

    const handleRegenerateSubmit = async (values: any) => {
        if (!record?.id) return;

        setRegenerating(true);
        try {
            const request: RegenerationRequest = {
                regenerationType: values.regenerationType,
                reason: values.reason,
                preserveOriginal: true,
                notifyCustomer: false,
            };

            const result = await invoiceRegenerationService.regenerateSaleReturn(record.id, request);

            if (result.success) {
                message.success(`Sale return ${record.id} regenerated successfully!`);
                setRegenerateModalVisible(false);
                form.resetFields();
                queryResult.refetch();
            } else {
                message.error(`Failed to regenerate sale return: ${result.errorMessage}`);
            }
        } catch (error: any) {
            console.error("Sale return regeneration error:", error);
            message.error(error?.response?.data?.message || error.message);
        } finally {
            setRegenerating(false);
        }
    };

    const handleModalCancel = () => {
        setRegenerateModalVisible(false);
        form.resetFields();
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Approved":
                return "blue";
            case "Draft":
                return "default";
            case "Completed":
                return "green";
            case "Rejected":
                return "red";
            default:
                return "default";
        }
    };

    const getSaleStatusColor = (status: string) => {
        switch (status) {
            case "PENDING":
                return "orange";
            case "COMPLETED":
                return "green";
            case "CANCELLED":
            case "RETURNED":
                return "red";
            default:
                return "default";
        }
    };

    const displayCustomerTPIN = (tpin?: string) => {
        if (!tpin || tpin === "0") {
            return <Text type="secondary" italic>Manual Entry</Text>;
        }
        return tpin;
    };

    const formatCurrency = (value: number | undefined) => {
        if (value === undefined || value === null) return "0.00";
        return value.toFixed(2);
    };

    // Build actions
    const actions: ActionButton[] = [];

    if (record?.documentUrl) {
        actions.push({
            label: "Print",
            onClick: handleViewDocument,
            type: "default",
            icon: <PrinterOutlined />,
            key: "print",
        });
    }

    if (record?.status === "Draft") {
        actions.push({
            label: "Approve",
            onClick: handleApprove,
            type: "primary",
            icon: <CheckCircleOutlined />,
            key: "approve",
        });
    }

    if (record?.status === "Approved") {
        actions.push({
            label: "Complete",
            onClick: handleComplete,
            type: "primary",
            icon: <CheckCircleOutlined />,
            key: "complete",
        });
    }

    // Build more menu items
    const moreMenuItems: ActionButton[] = [];

    if (record?.documentUrl) {
        moreMenuItems.push({
            key: "regenerate",
            label: "Regenerate",
            onClick: handleOpenRegenerate,
            icon: <SyncOutlined />,
        });
    }

    if (record?.status === "Draft") {
        moreMenuItems.push({
            key: "reject",
            label: "Reject",
            onClick: handleReject,
            icon: <CloseCircleOutlined />,
            danger: true,
        });
    }

    if (isLoading) {
        return <div className="page-container">Loading...</div>;
    }

    return (
        <div className="page-container">
            <PageHeader
                title={`Sale Return #${record?.id}`}
                icon={<RollbackOutlined />}
                breadcrumbs={[
                    { title: "Sale Returns", href: "/sale-returns" },
                    { title: `#${record?.id}` },
                ]}
                actions={actions}
                moreMenuItems={moreMenuItems.length > 0 ? moreMenuItems : undefined}
            >
                <div className="mini-stat-cards-row">
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Return Amount</div>
                        <div className="mini-stat-value">${formatCurrency(record?.sale?.totalAmount)}</div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Status</div>
                        <div className={`status-badge ${record?.status === "Completed" ? "good" : record?.status === "Approved" ? "pending" : record?.status === "Draft" ? "pending" : "inactive"}`}>
                            {record?.status === "Completed" && <CheckCircleOutlined />}
                            {record?.status === "Approved" && <ClockCircleOutlined />}
                            {record?.status === "Draft" && <ClockCircleOutlined />}
                            {record?.status === "Rejected" && <CloseCircleOutlined />}
                            {record?.status}
                        </div>
                    </div>
                    <div className="mini-stat-card">
                        <div className="mini-stat-label">Currency</div>
                        <div className="mini-stat-value">{record?.sale?.currencyTypeCode}</div>
                    </div>
                </div>
            </PageHeader>

            {/* Return Details */}
            <div className="info-cards-grid">
                <div className="info-card">
                    <div className="info-card-header">Return Information</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Return ID:</span>
                            <span className="info-value">{record?.id}</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Status:</span>
                            <span className="info-value">
                                <Tag color={getStatusColor(record?.status)}>{record?.status}</Tag>
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Return Date:</span>
                            <span className="info-value"><DateField value={record?.returnDate} format="YYYY-MM-DD" /></span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Return Reason:</span>
                            <span className="info-value">{record?.returnReason || '-'}</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-card-header">Original Sale</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Invoice Number:</span>
                            <span className="info-value">
                                <Link to={`/sales/show/${record?.saleId}`} style={{ color: 'var(--color-primary)' }}>
                                    {record?.sale?.invoiceNo}
                                </Link>
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Sale Status:</span>
                            <span className="info-value">
                                <Tag color={getSaleStatusColor(record?.sale?.status)}>{record?.sale?.status}</Tag>
                            </span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-card-header">Customer Details</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Customer Name:</span>
                            <span className="info-value">
                                {record?.sale?.customerId ? (
                                    <Link to={`/customers/show/${record.sale.customerId}`} style={{ color: 'var(--color-primary)' }}>
                                        {record?.sale?.customerName}
                                    </Link>
                                ) : (
                                    record?.sale?.customerName || '-'
                                )}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Customer TPIN:</span>
                            <span className="info-value">{displayCustomerTPIN(record?.sale?.customerTPIN)}</span>
                        </div>
                    </div>
                </div>

                <div className="info-card">
                    <div className="info-card-header">System Information</div>
                    <div className="info-card-content">
                        <div className="info-item">
                            <span className="info-label">Created At:</span>
                            <span className="info-value"><DateField value={record?.createdAt} format="YYYY-MM-DD HH:mm" /></span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Updated At:</span>
                            <span className="info-value"><DateField value={record?.updatedAt} format="YYYY-MM-DD HH:mm" /></span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Sale Items */}
            <div className="data-table-card">
                <Card
                    title={
                        <Space>
                            <span>Sale Items</span>
                            <Badge count={record?.sale?.items?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
                        </Space>
                    }
                >
                    {record?.sale?.items && record.sale.items.length > 0 ? (
                        <Table
                            dataSource={record.sale.items}
                            columns={[
                                {
                                    title: 'Item Code',
                                    key: 'itemCode',
                                    render: (_: any, item: any) => {
                                        const itemCode = item?.inventoryItem?.itemCode || item?.itemCode || 'N/A';
                                        const inventoryItemId = item?.inventoryItemId;
                                        return inventoryItemId ? (
                                            <Link to={`/products/show/${inventoryItemId}`} style={{ color: 'var(--color-primary)' }}>
                                                {itemCode}
                                            </Link>
                                        ) : itemCode;
                                    }
                                },
                                {
                                    title: 'Item Name',
                                    dataIndex: 'itemName',
                                    key: 'itemName',
                                    render: (_: any, item: any) => item?.inventoryItem?.itemName || item?.itemName || 'N/A'
                                },
                                {
                                    title: 'Quantity',
                                    dataIndex: 'quantity',
                                    key: 'quantity',
                                    render: (value: number) => value?.toFixed(2)
                                },
                                {
                                    title: 'Unit Price',
                                    dataIndex: 'unitPrice',
                                    key: 'unitPrice',
                                    render: (value: number) => `$${value?.toFixed(2)}`
                                },
                                {
                                    title: 'Total Amount',
                                    dataIndex: 'totalAmount',
                                    key: 'totalAmount',
                                    render: (value: number) => `$${value?.toFixed(2)}`
                                }
                            ]}
                            rowKey={(record) => record.id.toString()}
                            pagination={false}
                        />
                    ) : (
                        <Text type="secondary">No item information available</Text>
                    )}
                </Card>
            </div>

            {/* Payments and Taxes */}
            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <div className="data-table-card">
                        <Card
                            title={
                                <Space>
                                    <span>Payments</span>
                                    <Badge count={record?.sale?.payments?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
                                </Space>
                            }
                        >
                            {record?.sale?.payments && record.sale.payments.length > 0 ? (
                                <Table
                                    dataSource={record.sale.payments}
                                    columns={[
                                        {
                                            title: 'Payment Method',
                                            dataIndex: 'paymentMethod',
                                            key: 'paymentMethod'
                                        },
                                        {
                                            title: 'Amount',
                                            dataIndex: 'amount',
                                            key: 'amount',
                                            render: (value: number) => `$${value?.toFixed(2)}`
                                        },
                                        {
                                            title: 'Currency',
                                            dataIndex: 'currencyTypeCode',
                                            key: 'currencyTypeCode'
                                        }
                                    ]}
                                    rowKey={(record) => record.id.toString()}
                                    pagination={false}
                                    size="small"
                                />
                            ) : (
                                <Text type="secondary">No payment information available</Text>
                            )}
                        </Card>
                    </div>
                </Col>

                <Col xs={24} md={12}>
                    <div className="data-table-card">
                        <Card
                            title={
                                <Space>
                                    <span>Taxes</span>
                                    <Badge count={record?.sale?.taxes?.length || 0} style={{ backgroundColor: 'var(--color-primary)' }} />
                                </Space>
                            }
                        >
                            {record?.sale?.taxes && record.sale.taxes.length > 0 ? (
                                <Table
                                    dataSource={record.sale.taxes}
                                    columns={[
                                        {
                                            title: 'Tax Type',
                                            dataIndex: 'taxTypeCode',
                                            key: 'taxTypeCode'
                                        },
                                        {
                                            title: 'Taxable Amount',
                                            dataIndex: 'taxableAmount',
                                            key: 'taxableAmount',
                                            render: (value: number) => `$${value?.toFixed(2)}`
                                        },
                                        {
                                            title: 'Tax %',
                                            dataIndex: 'taxPercent',
                                            key: 'taxPercent',
                                            render: (value: number) => `${value?.toFixed(2)}%`
                                        },
                                        {
                                            title: 'Tax Amount',
                                            dataIndex: 'taxAmount',
                                            key: 'taxAmount',
                                            render: (value: number) => `$${value?.toFixed(2)}`
                                        }
                                    ]}
                                    rowKey={(record) => record.id.toString()}
                                    pagination={false}
                                    size="small"
                                />
                            ) : (
                                <Text type="secondary">No tax information available</Text>
                            )}
                        </Card>
                    </div>
                </Col>
            </Row>

            {/* Summary */}
            <div className="info-card" style={{ marginTop: 16 }}>
                <div className="info-card-header">Financial Summary</div>
                <div className="info-card-content">
                    <Row gutter={[24, 16]}>
                        <Col xs={12} sm={6}>
                            <div className="info-item">
                                <span className="info-label">Total Items:</span>
                                <span className="info-value">{record?.sale?.totalItemCount || 0}</span>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="info-item">
                                <span className="info-label">Taxable Amount:</span>
                                <span className="info-value">${formatCurrency(record?.sale?.totalTaxableAmount)}</span>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="info-item">
                                <span className="info-label">Total Tax:</span>
                                <span className="info-value">${formatCurrency(record?.sale?.totalTax)}</span>
                            </div>
                        </Col>
                        <Col xs={12} sm={6}>
                            <div className="info-item">
                                <span className="info-label">Total Amount:</span>
                                <span className="info-value" style={{ fontWeight: 'bold', color: 'var(--color-primary)', fontSize: '18px' }}>
                                    ${formatCurrency(record?.sale?.totalAmount)}
                                </span>
                            </div>
                        </Col>
                    </Row>
                </div>
            </div>

            {/* Regenerate Modal */}
            <Modal
                title={`Regenerate Sale Return ${record?.sale?.invoiceNo ? `(${record.sale.invoiceNo})` : ""}`}
                open={regenerateModalVisible}
                onCancel={handleModalCancel}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleRegenerateSubmit}
                    initialValues={{ regenerationType: "Manual", reason: "" }}
                >
                    <Form.Item
                        label="Regeneration Type"
                        name="regenerationType"
                        rules={[{ required: true, message: "Please select a regeneration type" }]}
                    >
                        <Select placeholder="Select a type">
                            <Select.Option value="Manual">Manual</Select.Option>
                            <Select.Option value="BugFix">Bug Fix</Select.Option>
                            <Select.Option value="LayoutUpdate">Layout Update</Select.Option>
                            <Select.Option value="DataCorrection">Data Correction</Select.Option>
                            <Select.Option value="Compliance">Compliance</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label="Reason"
                        name="reason"
                        rules={[{ required: true, message: "Please provide a reason for regeneration" }]}
                    >
                        <TextArea rows={4} placeholder="Describe why this sale return document needs regeneration" />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={regenerating}>
                                Regenerate
                            </Button>
                            <Button onClick={handleModalCancel}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
